// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/Cases/CasesPage.tsx
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import "./CasesPage.css";
import { useNavigatorDialog } from "../useNavigatorDialog";
import { portalFetch } from "../../../../utils/portalRoleChoice";

type CasesLocationState = {
  casesSearchQ?: string;
  casesReturnTo?: string;
};

type NavigatorCaseItem = {
  navigatorCaseId: number;
  patientLeadId: number;
  status: string;
  nextActionCode?: string | null;
  nextActionLabel?: string | null;
  sourceType: string;
  sourceLabel: string | null;
  assignedNavigatorUserId: number | null;
  assignedNavigatorName: string | null;
  patientFsa: string | null;
  patientCityId: number | null;
  patientCityName: string | null;
  ageBand: string | null;
  gender: string | null;
  preferredLanguageId?: number | null;
  preferredLanguageName: string | null;
  priorityLevel: string | null;
  priorityReasons?: unknown[];
  priorityReasonsJson?: string | null;
  selectedFamilyDoctorName: string | null;
  selectedClinicName: string | null;
  submittedToDoctorAt: string | null;
  latestProviderRequestStatus?: string | null;
  latestProviderRequestStatusLabel?: string | null;
  providerResponseLabel?: string | null;
  latestProviderRequest?: {
    statusName?: string | null;
    rawStatusName?: string | null;
    statusLabel?: string | null;
    familyDoctorName?: string | null;
    clinicName?: string | null;
    forwardedAt?: string | null;
    submittedAt?: string | null;
    decisionAt?: string | null;
    attachmentCompletedAt?: string | null;
  } | null;
  closedAt: string | null;
  closedReason: string | null;
  createdAt: string;
  updatedAt: string | null;
  lastActivityAt: string | null;
  lastActivityType: string | null;
  lastActivityOutcomeCode: string | null;
  outstandingPatientContactTaskCount?: number;
  phoneFollowUpRequired?: boolean;
  phoneFollowUpQueueLabel?: string | null;
  patient: {
    fullName: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    postalCode: string | null;
    email: string | null;
    phoneNumber: string | null;
    applicantEmail: string | null;
    applicantPhone: string | null;
    manualNoEmail?: boolean;
    navigatorContactPhone?: string | null;
  };
};

type CasesResponse = {
  ok: boolean;
  mode?: string;
  user?: {
    userId: number;
    role: string;
    ohtId: number;
    ohtName: string | null;
    ohtCode: string | null;
  };
  paging?: {
    total: number;
    limit: number;
    offset: number;
  };
  filters?: {
    status: string | null;
    assignedToMe: boolean;
    unassigned?: boolean;
    active?: boolean;
    nextAction?: string | null;
    nextActionGroup?: string | null;
    q: string | null;
    sourceType: string | null;
    priorityLevel: string | null;
    uploadBatchId?: number | null;
  };
  items?: NavigatorCaseItem[];
  error?: string;
};

type WorkQueueCounts = {
  allActive: number;
  intakeNeeded: number;
  readyToMatch: number;
  selectProvider: number;
  waitingForClinic: number;
  followUpRematch: number;
  phoneFollowUp: number;
  casesToClose: number;
  closed: number;
};

type CaseScopeCounts = {
  mine: number;
  all: number;
  unassigned: number;
};

type CasesWorkloadSummary = {
  ok: boolean;
  scopeCounts?: CaseScopeCounts;
  workQueuesByScope?: Partial<Record<CaseScope, WorkQueueCounts>>;
  error?: string;
};

type CreateCaseResponse = {
  ok: boolean;
  navigatorCaseId?: number;
  patientLeadId?: number;
  reusedPatientLead?: boolean;
  manualNoEmail?: boolean;
  status?: string;
  sourceType?: string;
  patientFsa?: string;
  error?: string;
};

type AssignCaseResponse = {
  ok: boolean;
  alreadyAssigned?: boolean;
  item?: {
    navigatorCaseId: number;
    assignedNavigatorUserId: number | null;
    assignedNavigatorName: string | null;
    status: string;
  };
  error?: string;
};

type WorkQueueId =
  | "allActive"
  | "intakeNeeded"
  | "readyToMatch"
  | "selectProvider"
  | "waitingForClinic"
  | "followUpRematch"
  | "phoneFollowUp"
  | "casesToClose"
  | "closed";

type WorkQueueOption = {
  id: WorkQueueId;
  label: string;
};

type SourceFilterOption = {
  label: string;
  value: string;
};

type PriorityFilterOption = {
  label: string;
  value: string;
};

type CaseScope = "mine" | "all" | "unassigned";

type ScopeOption = {
  label: string;
  value: CaseScope;
};

type ManualCaseForm = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  postalCode: string;
  patientCityId: string;
  phoneNumber: string;
  email: string;
  gender: string;
  priorityLevel: string;
  sourceLabel: string;
  note: string;
};

type CityOption = {
  CityID: number;
  Name: string;
};

const PAGE_SIZE = 50;

const workQueues: WorkQueueOption[] = [
  { id: "allActive", label: "All active" },
  { id: "intakeNeeded", label: "Intake needed" },
  { id: "readyToMatch", label: "Ready to match" },
  { id: "selectProvider", label: "Select provider" },
  { id: "waitingForClinic", label: "Waiting for clinic" },
  { id: "followUpRematch", label: "Follow-up / rematch" },
  { id: "phoneFollowUp", label: "Phone follow-up" },
  { id: "casesToClose", label: "Cases to close" },
  { id: "closed", label: "Closed" },
];

const LEGACY_CASE_STATUSES = new Set([
  "New",
  "Assigned",
  "InProgress",
  "IntakeComplete",
  "MatchReady",
  "ForwardedToProvider",
  "Closed",
]);

function isTruthyQueryParam(raw: string | null): boolean {
  const value = String(raw || "")
    .trim()
    .toLowerCase();
  return value === "1" || value === "true" || value === "yes";
}

const sourceFilterOptions: SourceFilterOption[] = [
  { label: "All", value: "" },
  { label: "Manual entry", value: "ManualNavigatorEntry" },
  { label: "Uploaded list", value: "NavigatorUpload" },
  { label: "Public navigator request", value: "NavigatorPublicRequest" },
];

const priorityFilterOptions: PriorityFilterOption[] = [
  { label: "All priorities", value: "" },
  { label: "Urgent", value: "Urgent" },
  { label: "High", value: "High" },
  { label: "Medium", value: "Medium" },
  { label: "Low", value: "Low" },
];

const caseScopeOptions: ScopeOption[] = [
  { label: "My cases", value: "mine" },
  { label: "All OHT cases", value: "all" },
  { label: "Unassigned", value: "unassigned" },
];

const emptyManualCaseForm: ManualCaseForm = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  postalCode: "",
  patientCityId: "",
  phoneNumber: "",
  email: "",
  gender: "",
  priorityLevel: "Medium",
  sourceLabel: "Manual navigator entry",
  note: "",
};

function normalizeCityOptions(payload: unknown): CityOption[] {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { cities?: unknown })?.cities)
      ? (payload as { cities: unknown[] }).cities
      : [];

  return rows
    .map((row) => {
      const record = row as Record<string, unknown>;
      return {
        CityID: Number(record.CityID ?? record.cityId ?? record.id),
        Name: String(record.Name ?? record.name ?? "").trim(),
      };
    })
    .filter(
      (city) =>
        Number.isFinite(city.CityID) && city.CityID > 0 && city.Name.length > 0
    )
    .sort((a, b) => a.Name.localeCompare(b.Name));
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatStatus(status: string | null | undefined): string {
  switch (status) {
    case "New":
      return "New";
    case "Assigned":
      return "Assigned";
    case "InProgress":
      return "In progress";
    case "IntakeComplete":
      return "Intake complete";
    case "MatchReady":
      return "Match ready";
    case "ForwardedToProvider":
      return "Forwarded";
    case "Closed":
      return "Closed";
    default:
      return status || "—";
  }
}

function normalizeProviderStatusKey(value: string | null | undefined): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
}

function formatProviderRequestStatus(status: string | null | undefined): string {
  const key = normalizeProviderStatusKey(status);

  if (!key) return "Not forwarded yet";
  if (key === "submitted") return "Awaiting clinic review";
  if (key === "inreview") return "Under review";
  if (key === "waitlist" || key === "waitlisted") return "Waitlisted by clinic";
  if (key === "accepted") return "Invited for Meet & Greet";
  if (key === "declined" || key === "notaccepted") {
    return "Clinic declined request";
  }
  if (key === "attachmentcomplete" || key === "attachmentcompleted") {
    return "Attachment completed";
  }
  if (key === "expired") return "Expired — no clinic response";
  if (key === "cancelled" || key === "canceled") return "Cancelled";

  if (
    key === "closedpatientwithdrew" ||
    key === "patientwithdrew" ||
    key === "patientdeclined" ||
    key === "closedpatientdeclined"
  ) {
    return "Cancelled";
  }

  if (
    key === "closedacceptedelsewhere" ||
    key === "patientacceptedelsewhere" ||
    key === "acceptedelsewhere" ||
    key === "closedpatientacceptedelsewhere"
  ) {
    return "Cancelled";
  }

  if (
    key === "closednoshow" ||
    key === "noshow" ||
    key === "didnotattend" ||
    key === "meetgreetnotcompleted" ||
    key === "closedmeetgreetnotcompleted"
  ) {
    return "Cancelled";
  }

  if (
    key === "closedclinicdeclinedaftermeetandgreet" ||
    key === "clinicdeclinedaftermeetandgreet" ||
    key === "closedclinicdeclinedaftermeetgreet" ||
    key === "clinicdeclinedaftermeetgreet"
  ) {
    return "Clinic declined request";
  }

  return status || "Not forwarded yet";
}

function getProviderResponseLabel(item: NavigatorCaseItem): string {
  const directLabel =
    item.providerResponseLabel ||
    item.latestProviderRequestStatusLabel ||
    item.latestProviderRequest?.statusLabel ||
    null;

  if (directLabel) {
    return directLabel === "Declined by clinic"
      ? "Clinic declined request"
      : directLabel;
  }

  const status =
    item.latestProviderRequestStatus ||
    item.latestProviderRequest?.statusName ||
    item.latestProviderRequest?.rawStatusName ||
    null;

  if (status) {
    return formatProviderRequestStatus(status);
  }

  return item.submittedToDoctorAt ? "Awaiting clinic review" : "Not forwarded yet";
}

function getProviderResponseClassName(label: string): string {
  const slug = getClassSlug(label);
  return slug
    ? `cn-cases-provider-response is-${slug}`
    : "cn-cases-provider-response";
}

function formatSource(sourceType: string | null | undefined): string {
  switch (sourceType) {
    case "NavigatorPublicRequest":
      return "Public navigator request";
    case "NavigatorUpload":
      return "Uploaded list";
    case "OHT":
      return "OHT list";
    case "ManualNavigatorEntry":
      return "Manual entry";
    case "FutureHCC":
      return "Future HCC";
    default:
      return sourceType || "—";
  }
}

function formatPatientName(item: NavigatorCaseItem): string {
  return (
    item.patient.fullName ||
    [item.patient.firstName, item.patient.lastName].filter(Boolean).join(" ") ||
    "Unnamed patient"
  );
}

function formatArea(item: NavigatorCaseItem): string {
  const fsa = item.patientFsa?.trim() || "";
  const city = item.patientCityName?.trim() || "";

  if (fsa && city) return `${fsa} · ${city}`;
  return fsa || city || "—";
}

function formatPatientMeta(item: NavigatorCaseItem): string {
  const ageBand = item.ageBand?.trim() || "";
  const caseLabel = `Case #${item.navigatorCaseId}`;
  return ageBand ? `${ageBand} · ${caseLabel}` : caseLabel;
}

function humanizeKey(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function getClassSlug(value: string | null | undefined): string {
  return String(value || "")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function getStatusClassName(status: string | null | undefined): string {
  const slug = getClassSlug(status);
  return slug ? `cn-cases-status is-${slug}` : "cn-cases-status";
}

function getPriorityClassName(priority: string | null | undefined): string {
  const slug = getClassSlug(priority);
  return slug
    ? `cn-cases-priority-badge is-${slug}`
    : "cn-cases-priority-badge";
}

function isInternalNoEmailAddress(value: string | null | undefined): boolean {
  return Boolean(value?.includes("@internal.acceptingmd.local"));
}

function isManualNoEmailCase(item: NavigatorCaseItem): boolean {
  return Boolean(
    item.patient.manualNoEmail || isInternalNoEmailAddress(item.patient.email)
  );
}

function getQueuePatientPhone(item: NavigatorCaseItem): string | null {
  const phone =
    item.patient.navigatorContactPhone ||
    item.patient.phoneNumber ||
    item.patient.applicantPhone ||
    null;
  const trimmed = String(phone || "").trim();
  return trimmed || null;
}

function getQueueNextActionDisplay(item: NavigatorCaseItem): string | null {
  if (
    item.phoneFollowUpRequired ||
    Number(item.outstandingPatientContactTaskCount || 0) > 0
  ) {
    return (
      item.phoneFollowUpQueueLabel ||
      (Number(item.outstandingPatientContactTaskCount || 0) > 1
        ? `${item.outstandingPatientContactTaskCount} phone follow-ups required`
        : item.nextActionCode === "ContactPatientForMeetAndGreet"
          ? "Meet & Greet — call patient"
          : item.nextActionCode === "ContactPatientAboutWaitlist"
            ? "Waitlist — call patient"
            : "Phone follow-up required")
    );
  }
  if (item.nextActionCode === "ContactPatientForMeetAndGreet") {
    return "Meet & Greet — call patient";
  }
  if (item.nextActionCode === "ContactPatientAboutWaitlist") {
    return "Waitlist — call patient";
  }
  return item.nextActionLabel || null;
}

function isPhoneFollowUpQueueItem(item: NavigatorCaseItem): boolean {
  return (
    item.phoneFollowUpRequired === true ||
    Number(item.outstandingPatientContactTaskCount || 0) > 0 ||
    item.nextActionCode === "ContactPatientForMeetAndGreet" ||
    item.nextActionCode === "ContactPatientAboutWaitlist"
  );
}

function getLastActivityLabel(item: NavigatorCaseItem): string | null {
  if (!item.lastActivityType && !item.lastActivityOutcomeCode) return null;

  const parts = [
    item.lastActivityType ? humanizeKey(item.lastActivityType) : null,
    item.lastActivityOutcomeCode
      ? humanizeKey(item.lastActivityOutcomeCode)
      : null,
  ].filter(Boolean);

  return parts.length ? parts.join(" · ") : null;
}

function getQueueLastActivity(item: NavigatorCaseItem): {
  label: string;
  timestamp: string | null;
} {
  const activityLabel = getLastActivityLabel(item);

  if (activityLabel) {
    return {
      label: activityLabel,
      timestamp: item.lastActivityAt || null,
    };
  }

  return {
    label: "Created",
    timestamp: item.createdAt || null,
  };
}

function getAssigneeLabel(
  item: NavigatorCaseItem,
  currentUserId: number | null
): string {
  if (!item.assignedNavigatorUserId) return "Unassigned";

  if (
    currentUserId !== null &&
    item.assignedNavigatorUserId === currentUserId
  ) {
    return "You";
  }

  return item.assignedNavigatorName || "Assigned navigator";
}

function getNextActionClassName(code: string | null | undefined): string {
  const slug = getClassSlug(code);
  return slug ? `cn-cases-next-action is-${slug}` : "cn-cases-next-action";
}

function parseUploadBatchId(raw: string | null): number | null {
  if (!raw) return null;

  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const n = Number(trimmed);
  if (!Number.isSafeInteger(n) || n < 1) return null;

  return n;
}

function parseCaseScope(raw: string | null): CaseScope {
  const value = String(raw || "")
    .trim()
    .toLowerCase();

  if (value === "all" || value === "unassigned" || value === "mine") {
    return value;
  }

  return "mine";
}

const VALID_WORK_QUEUE_NEXT_ACTIONS = new Set([
  "CompleteIntake",
  "RunMatching",
  "SelectProvider",
  "WaitingForClinic",
  "CloseCase",
]);

function shouldApplyDefaultMyCasesQueue(params: URLSearchParams): boolean {
  if (params.get("status") === "Closed") return false;
  if (params.has("active")) return false;
  if (isTruthyQueryParam(params.get("intakeNotStarted"))) return false;
  if (isTruthyQueryParam(params.get("phoneFollowUpRequired"))) return false;

  const scope = String(params.get("scope") || "")
    .trim()
    .toLowerCase();
  if (scope === "all" || scope === "unassigned") return false;

  return true;
}

function parseNextActionFilter(raw: string | null): string | null {
  const value = String(raw || "").trim();
  if (!VALID_WORK_QUEUE_NEXT_ACTIONS.has(value)) return null;
  return value;
}

function parseNextActionGroupFilter(raw: string | null): string | null {
  const value = String(raw || "").trim();
  return value === "followUpRematch" ? value : null;
}

function parseLegacyStatusFilter(raw: string | null): string | null {
  const value = String(raw || "").trim();
  if (!value || !LEGACY_CASE_STATUSES.has(value)) return null;
  return value;
}

function resolveSelectedWorkQueue(args: {
  status: string | null;
  nextAction: string | null;
  nextActionGroup: string | null;
  phoneFollowUpRequired: boolean;
  intakeNotStarted: boolean;
}): WorkQueueId | null {
  if (args.intakeNotStarted) return null;
  if (args.phoneFollowUpRequired) return "phoneFollowUp";
  if (args.nextActionGroup === "followUpRematch") return "followUpRematch";
  if (args.nextAction === "CompleteIntake") return "intakeNeeded";
  if (args.nextAction === "RunMatching") return "readyToMatch";
  if (args.nextAction === "SelectProvider") return "selectProvider";
  if (args.nextAction === "WaitingForClinic") return "waitingForClinic";
  if (args.nextAction === "CloseCase") return "casesToClose";
  if (args.status === "Closed") return "closed";
  if (args.status) return null;
  return "allActive";
}

function parseSourceTypeFilter(raw: string | null): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  return sourceFilterOptions.some((option) => option.value === value)
    ? value
    : "";
}

function parsePriorityLevelFilter(raw: string | null): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  return priorityFilterOptions.some((option) => option.value === value)
    ? value
    : "";
}

function parsePageParam(raw: string | null): number {
  if (!raw) return 1;

  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return 1;

  const n = Number(trimmed);
  if (!Number.isSafeInteger(n) || n < 1) return 1;

  return n;
}

function readCasesLocationState(state: unknown): CasesLocationState {
  if (!state || typeof state !== "object") return {};
  return state as CasesLocationState;
}

function getAppliedCasesSearchQ(state: unknown): string {
  const raw = readCasesLocationState(state).casesSearchQ;
  return typeof raw === "string" ? raw : "";
}

function workQueueLabel(id: WorkQueueId | null): string | null {
  if (!id) return null;
  return workQueues.find((queue) => queue.id === id)?.label || null;
}

function formatWorkloadCount(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return value.toLocaleString();
}

function buildApiUrl(
  status: string | null,
  q: string,
  sourceType: string,
  priorityLevel: string,
  offset: number,
  uploadBatchId: number | null,
  caseScope: CaseScope,
  activeOnly: boolean,
  nextAction: string | null,
  nextActionGroup: string | null,
  intakeNotStarted: boolean,
  phoneFollowUpRequired: boolean
): string {
  const params = new URLSearchParams();

  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String(Math.max(0, offset)));

  if (caseScope === "mine") {
    params.set("assignedToMe", "1");
  } else if (caseScope === "unassigned") {
    params.set("unassigned", "1");
  }

  if (activeOnly) {
    params.set("active", "1");
  }

  if (intakeNotStarted) {
    params.set("intakeNotStarted", "1");
  }

  if (phoneFollowUpRequired) {
    params.set("phoneFollowUpRequired", "1");
  } else if (nextAction) {
    params.set("nextAction", nextAction);
  } else if (nextActionGroup) {
    params.set("nextActionGroup", nextActionGroup);
  }

  if (status) {
    params.set("status", status);
  }

  const trimmedQ = q.trim();
  if (trimmedQ) {
    params.set("q", trimmedQ);
  }

  if (sourceType) {
    params.set("sourceType", sourceType);
  }

  if (priorityLevel) {
    params.set("priorityLevel", priorityLevel);
  }

  if (uploadBatchId !== null) {
    params.set("uploadBatchId", String(uploadBatchId));
  }

  return `/api/portal/community-navigator/cases?${params.toString()}`;
}

function normalizeNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export default function CasesPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const activeUploadBatchId = useMemo(
    () => parseUploadBatchId(searchParams.get("uploadBatchId")),
    [searchParams]
  );
  const activeCaseScope = useMemo(
    () => parseCaseScope(searchParams.get("scope")),
    [searchParams]
  );
  const activeNextAction = useMemo(
    () => parseNextActionFilter(searchParams.get("nextAction")),
    [searchParams]
  );
  const activeNextActionGroup = useMemo(
    () => parseNextActionGroupFilter(searchParams.get("nextActionGroup")),
    [searchParams]
  );
  const phoneFollowUpRequiredFilter = useMemo(
    () => isTruthyQueryParam(searchParams.get("phoneFollowUpRequired")),
    [searchParams]
  );
  const legacyStatus = useMemo(
    () => parseLegacyStatusFilter(searchParams.get("status")),
    [searchParams]
  );
  const intakeNotStartedFilter = useMemo(
    () => isTruthyQueryParam(searchParams.get("intakeNotStarted")),
    [searchParams]
  );
  const selectedWorkQueue = useMemo(
    () =>
      resolveSelectedWorkQueue({
        status: legacyStatus,
        nextAction: activeNextAction,
        nextActionGroup: activeNextActionGroup,
        phoneFollowUpRequired: phoneFollowUpRequiredFilter,
        intakeNotStarted: intakeNotStartedFilter,
      }),
    [
      legacyStatus,
      activeNextAction,
      activeNextActionGroup,
      phoneFollowUpRequiredFilter,
      intakeNotStartedFilter,
    ]
  );
  const selectedSourceType = useMemo(
    () => parseSourceTypeFilter(searchParams.get("sourceType")),
    [searchParams]
  );
  const selectedPriorityLevel = useMemo(
    () => parsePriorityLevelFilter(searchParams.get("priorityLevel")),
    [searchParams]
  );
  const currentPage = useMemo(
    () => parsePageParam(searchParams.get("page")),
    [searchParams]
  );
  const appliedSearchText = useMemo(
    () => getAppliedCasesSearchQ(location.state),
    [location.state]
  );
  // Work queues other than Closed exclude closed cases. A legacy lifecycle
  // status bookmark keeps that exact predicate and does not also force active=1.
  const queueActiveOnly =
    selectedWorkQueue !== null && selectedWorkQueue !== "closed";

  const [searchText, setSearchText] = useState(() =>
    getAppliedCasesSearchQ(location.state)
  );
  const [data, setData] = useState<CasesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [manualCaseForm, setManualCaseForm] =
    useState<ManualCaseForm>(emptyManualCaseForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [manualCityOptions, setManualCityOptions] = useState<CityOption[]>([]);
  const [isLoadingManualCities, setIsLoadingManualCities] = useState(false);
  const [manualCitiesError, setManualCitiesError] = useState<string | null>(
    null
  );
  const createDialogRef = useRef<HTMLElement>(null);
  const createFirstNameRef = useRef<HTMLInputElement>(null);
  const createLastNameRef = useRef<HTMLInputElement>(null);
  const createDobRef = useRef<HTMLInputElement>(null);
  const createPostalRef = useRef<HTMLInputElement>(null);
  const createPhoneRef = useRef<HTMLInputElement>(null);
  const createErrorRef = useRef<HTMLDivElement>(null);
  const createSuccessRef = useRef<HTMLElement>(null);
  const suppressCreateReturnFocusRef = useRef(false);
  const pendingCreateSuccessFocusRef = useRef(false);
  const [assigningCaseId, setAssigningCaseId] = useState<number | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [workloadSummary, setWorkloadSummary] =
    useState<CasesWorkloadSummary | null>(null);

  const offset = (currentPage - 1) * PAGE_SIZE;

  const navigateCasesQueue = useCallback(
    (
      mutate: (params: URLSearchParams) => void,
      options: {
        replace?: boolean;
        /** undefined = preserve current applied search; null = clear; string = set */
        casesSearchQ?: string | null;
      } = {}
    ) => {
      const next = new URLSearchParams(searchParams);
      mutate(next);

      if (next.get("page") === "1") {
        next.delete("page");
      }

      let nextSearchQ: string | null;
      if (options.casesSearchQ === undefined) {
        const preserved = getAppliedCasesSearchQ(location.state).trim();
        nextSearchQ = preserved || null;
      } else if (options.casesSearchQ === null) {
        nextSearchQ = null;
      } else {
        const trimmed = options.casesSearchQ.trim();
        nextSearchQ = trimmed || null;
      }

      const nextState: CasesLocationState = nextSearchQ
        ? { casesSearchQ: nextSearchQ }
        : {};

      const search = next.toString();
      navigate(
        {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
        },
        {
          replace: options.replace === true,
          state: nextState,
        }
      );
    },
    [searchParams, location.state, location.pathname, navigate]
  );

  // Privacy: move accidental/manual ?q= into router state and strip from URL.
  // CN-R28: bare / My cases navigation excludes Closed (scope=mine&active=1).
  useEffect(() => {
    const hasQ = searchParams.has("q");
    const applyDefault = shouldApplyDefaultMyCasesQueue(searchParams);

    if (!hasQ && !applyDefault) return;

    const trimmed = String(searchParams.get("q") ?? "").trim();
    const preserved = getAppliedCasesSearchQ(location.state).trim();

    navigateCasesQueue(
      (next) => {
        if (hasQ) next.delete("q");
        if (applyDefault) {
          next.set("scope", "mine");
          next.set("active", "1");
        }
      },
      {
        replace: true,
        casesSearchQ: hasQ ? trimmed || preserved || null : undefined,
      }
    );
  }, [searchParams, location.state, navigateCasesQueue]);

  useEffect(() => {
    setSearchText(appliedSearchText);
  }, [appliedSearchText]);

  const loadCases = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await portalFetch(
        buildApiUrl(
          legacyStatus,
          appliedSearchText,
          selectedSourceType,
          selectedPriorityLevel,
          offset,
          activeUploadBatchId,
          activeCaseScope,
          queueActiveOnly,
          activeNextAction,
          activeNextActionGroup,
          intakeNotStartedFilter,
          phoneFollowUpRequiredFilter
        ),
        {
          method: "GET",
          credentials: "include",
        }
      );

      const payload = (await response.json()) as CasesResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to load navigator cases.");
      }

      setData(payload);
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : "Unable to load navigator cases."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    legacyStatus,
    appliedSearchText,
    selectedSourceType,
    selectedPriorityLevel,
    offset,
    activeUploadBatchId,
    activeCaseScope,
    queueActiveOnly,
    activeNextAction,
    activeNextActionGroup,
    intakeNotStartedFilter,
    phoneFollowUpRequiredFilter,
  ]);

  useEffect(() => {
    let isMounted = true;

    async function runLoadCases() {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await portalFetch(
          buildApiUrl(
            legacyStatus,
            appliedSearchText,
            selectedSourceType,
            selectedPriorityLevel,
            offset,
            activeUploadBatchId,
            activeCaseScope,
            queueActiveOnly,
            activeNextAction,
            activeNextActionGroup,
            intakeNotStartedFilter,
            phoneFollowUpRequiredFilter
          ),
          {
            method: "GET",
            credentials: "include",
          }
        );

        const payload = (await response.json()) as CasesResponse;

        if (!response.ok || !payload.ok) {
          throw new Error(payload?.error || "Unable to load navigator cases.");
        }

        if (isMounted) {
          setData(payload);
        }
      } catch (err) {
        if (isMounted) {
          setLoadError(
            err instanceof Error
              ? err.message
              : "Unable to load navigator cases."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    runLoadCases();

    return () => {
      isMounted = false;
    };
  }, [
    legacyStatus,
    appliedSearchText,
    selectedSourceType,
    selectedPriorityLevel,
    offset,
    activeUploadBatchId,
    activeCaseScope,
    queueActiveOnly,
    activeNextAction,
    activeNextActionGroup,
    intakeNotStartedFilter,
    phoneFollowUpRequiredFilter,
    reloadToken,
  ]);

  // One unfiltered summary for the filter cards. Search, source, priority,
  // the selected work queue, and upload batch do not change these counts.
  // reloadToken refreshes them after assign/create, which already reload the list.
  useEffect(() => {
    let isMounted = true;

    async function loadWorkloadSummary() {
      try {
        const response = await portalFetch(
          "/api/portal/community-navigator/cases?summary=1",
          {
            method: "GET",
            credentials: "include",
          }
        );

        const payload = (await response.json()) as CasesWorkloadSummary;

        if (!isMounted) return;

        if (!response.ok || !payload.ok) {
          setWorkloadSummary(null);
          return;
        }

        setWorkloadSummary(payload);
      } catch {
        if (isMounted) {
          setWorkloadSummary(null);
        }
      }
    }

    void loadWorkloadSummary();

    return () => {
      isMounted = false;
    };
  }, [reloadToken]);

  const items = useMemo(() => data?.items || [], [data?.items]);
  const total = data?.paging?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + items.length, total);
  const showPager = total > PAGE_SIZE;
  const currentUserId = data?.user?.userId || null;

  const caseDetailLinkState = useMemo((): CasesLocationState => {
    const state: CasesLocationState = {
      casesReturnTo: `${location.pathname}${location.search}`,
    };
    const trimmedQ = appliedSearchText.trim();
    if (trimmedQ) {
      state.casesSearchQ = trimmedQ;
    }
    return state;
  }, [location.pathname, location.search, appliedSearchText]);

  useEffect(() => {
    if (isLoading) return;

    if (total === 0) {
      if (currentPage !== 1) {
        navigateCasesQueue((next) => {
          next.delete("page");
        }, { replace: true });
      }
      return;
    }

    if (items.length === 0 && currentPage > 1) {
      const lastValidPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
      if (currentPage !== lastValidPage) {
        navigateCasesQueue((next) => {
          if (lastValidPage <= 1) {
            next.delete("page");
          } else {
            next.set("page", String(lastValidPage));
          }
        }, { replace: true });
      }
    }
  }, [
    isLoading,
    total,
    items.length,
    currentPage,
    navigateCasesQueue,
  ]);

  function handleSearchSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = searchText.trim();
    navigateCasesQueue(
      (next) => {
        next.delete("page");
      },
      { casesSearchQ: trimmed || null }
    );
  }

  function handleClearSearch() {
    setSearchText("");
    navigateCasesQueue(
      (next) => {
        next.delete("page");
      },
      { casesSearchQ: null }
    );
  }

  function handleClearFilters() {
    setSearchText("");
    navigateCasesQueue(
      (next) => {
        next.delete("status");
        next.delete("intakeNotStarted");
        next.delete("phoneFollowUpRequired");
        next.delete("nextAction");
        next.delete("nextActionGroup");
        next.set("active", "1");
        next.delete("sourceType");
        next.delete("priorityLevel");
        next.delete("page");
      },
      { casesSearchQ: null }
    );
  }

  function handleClearUploadBatchFilter() {
    navigateCasesQueue((next) => {
      next.delete("uploadBatchId");
      next.delete("page");
    });
  }

  function handleCaseScopeChange(scope: CaseScope) {
    navigateCasesQueue((next) => {
      next.set("scope", scope);
      next.delete("page");

      if (next.get("status") === "Closed") {
        next.delete("active");
      } else if (!next.get("status")) {
        next.set("active", "1");
      }
    });
  }

  function handleWorkQueueChange(queue: WorkQueueId) {
    navigateCasesQueue((next) => {
      next.delete("page");
      next.delete("status");
      next.delete("intakeNotStarted");
      next.delete("phoneFollowUpRequired");
      next.delete("nextAction");
      next.delete("nextActionGroup");
      next.delete("active");

      if (queue === "closed") {
        next.set("status", "Closed");
        return;
      }

      next.set("active", "1");

      if (queue === "intakeNeeded") {
        next.set("nextAction", "CompleteIntake");
      } else if (queue === "readyToMatch") {
        next.set("nextAction", "RunMatching");
      } else if (queue === "selectProvider") {
        next.set("nextAction", "SelectProvider");
      } else if (queue === "waitingForClinic") {
        next.set("nextAction", "WaitingForClinic");
      } else if (queue === "followUpRematch") {
        next.set("nextActionGroup", "followUpRematch");
      } else if (queue === "phoneFollowUp") {
        next.set("phoneFollowUpRequired", "1");
      } else if (queue === "casesToClose") {
        next.set("nextAction", "CloseCase");
      }
    });
  }

  function handleSourceTypeChange(value: string) {
    navigateCasesQueue((next) => {
      if (value) {
        next.set("sourceType", value);
      } else {
        next.delete("sourceType");
      }
      next.delete("page");
    });
  }

  function handlePriorityLevelChange(value: string) {
    navigateCasesQueue((next) => {
      if (value) {
        next.set("priorityLevel", value);
      } else {
        next.delete("priorityLevel");
      }
      next.delete("page");
    });
  }

  function handlePageChange(nextPage: number) {
    const safePage = Math.max(1, Math.min(totalPages, nextPage));
    navigateCasesQueue((next) => {
      if (safePage <= 1) {
        next.delete("page");
      } else {
        next.set("page", String(safePage));
      }
    });
  }

  function openCreateModal() {
    setManualCaseForm(emptyManualCaseForm);
    setCreateError(null);
    setCreateSuccess(null);
    setAssignError(null);
    setAssignSuccess(null);
    setIsCreateModalOpen(true);
    void loadManualCityOptions();
  }

  async function loadManualCityOptions() {
    setIsLoadingManualCities(true);
    setManualCitiesError(null);

    try {
      const response = await fetch("/api/cities", { credentials: "include" });
      if (!response.ok) {
        throw new Error("Unable to load cities.");
      }

      const payload = await response.json();
      setManualCityOptions(normalizeCityOptions(payload));
    } catch (err) {
      setManualCityOptions([]);
      setManualCitiesError(
        err instanceof Error ? err.message : "Unable to load cities."
      );
    } finally {
      setIsLoadingManualCities(false);
    }
  }

  function closeCreateModal() {
    if (isCreating) return;
    setIsCreateModalOpen(false);
    setCreateError(null);
  }

  function updateManualCaseForm<K extends keyof ManualCaseForm>(
    key: K,
    value: ManualCaseForm[K]
  ) {
    setManualCaseForm((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function validateManualCaseForm(): string | null {
    if (!manualCaseForm.firstName.trim()) return "First name is required.";
    if (!manualCaseForm.lastName.trim()) return "Last name is required.";
    if (!manualCaseForm.dateOfBirth.trim()) {
      return "Date of birth is required.";
    }
    if (!manualCaseForm.postalCode.trim()) return "Postal code is required.";

    if (!manualCaseForm.email.trim() && !manualCaseForm.phoneNumber.trim()) {
      return "Enter an email or phone number. Phone is required when email is missing.";
    }

    return null;
  }

  async function handleAssignToMe(navigatorCaseId: number) {
    setAssigningCaseId(navigatorCaseId);
    setAssignError(null);
    setAssignSuccess(null);
    setCreateSuccess(null);

    try {
      const response = await portalFetch(
        `/api/portal/community-navigator/cases/${navigatorCaseId}/assign`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const payload = (await response.json()) as AssignCaseResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to claim navigator case.");
      }

      setAssignSuccess(
        payload.alreadyAssigned
          ? `Case #${navigatorCaseId} was already assigned to you.`
          : `Case #${navigatorCaseId} claimed successfully.`
      );

      setReloadToken((value) => value + 1);
    } catch (err) {
      setAssignError(
        err instanceof Error ? err.message : "Unable to claim navigator case."
      );
      setReloadToken((value) => value + 1);
    } finally {
      setAssigningCaseId(null);
    }
  }

  async function handleCreateManualCase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const validationError = validateManualCaseForm();
    if (validationError) {
      setCreateError(validationError);
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    setCreateSuccess(null);
    setAssignError(null);
    setAssignSuccess(null);

    try {
      const response = await portalFetch(
        "/api/portal/community-navigator/cases/create",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: manualCaseForm.firstName.trim(),
            lastName: manualCaseForm.lastName.trim(),
            dateOfBirth: manualCaseForm.dateOfBirth,
            postalCode: manualCaseForm.postalCode.trim(),
            patientCityId: manualCaseForm.patientCityId
              ? Number.parseInt(manualCaseForm.patientCityId, 10)
              : null,
            phoneNumber: normalizeNullable(manualCaseForm.phoneNumber),
            email: normalizeNullable(manualCaseForm.email),
            gender: normalizeNullable(manualCaseForm.gender),
            priorityLevel: normalizeNullable(manualCaseForm.priorityLevel),
            sourceLabel:
              normalizeNullable(manualCaseForm.sourceLabel) ||
              "Manual navigator entry",
            note: normalizeNullable(manualCaseForm.note),
          }),
        }
      );

      const payload = (await response.json()) as CreateCaseResponse;

      if (!response.ok || !payload.ok) {
        throw new Error(payload?.error || "Unable to create navigator case.");
      }

      setCreateSuccess(
        `Navigator case #${payload.navigatorCaseId} created successfully.`
      );
      suppressCreateReturnFocusRef.current = true;
      pendingCreateSuccessFocusRef.current = true;
      setManualCaseForm(emptyManualCaseForm);
      setIsCreateModalOpen(false);
      setReloadToken((value) => value + 1);

      if (
        selectedWorkQueue !== "allActive" &&
        selectedWorkQueue !== "intakeNeeded"
      ) {
        navigateCasesQueue((next) => {
          next.delete("status");
          next.delete("intakeNotStarted");
          next.delete("phoneFollowUpRequired");
          next.delete("nextAction");
          next.delete("nextActionGroup");
          next.set("active", "1");
          next.delete("page");
        });
      } else {
        await loadCases();
      }
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Unable to create navigator case."
      );
    } finally {
      setIsCreating(false);
    }
  }

  useNavigatorDialog({
    open: isCreateModalOpen,
    onDismiss: closeCreateModal,
    dismissDisabled: isCreating,
    dialogRef: createDialogRef,
    initialFocusRef: createFirstNameRef,
    suppressReturnFocusRef: suppressCreateReturnFocusRef,
  });

  useLayoutEffect(() => {
    if (!isCreateModalOpen || !createError) return;

    if (createError.startsWith("First name")) {
      createFirstNameRef.current?.focus();
      return;
    }
    if (createError.startsWith("Last name")) {
      createLastNameRef.current?.focus();
      return;
    }
    if (createError.startsWith("Date of birth")) {
      createDobRef.current?.focus();
      return;
    }
    if (createError.startsWith("Postal code")) {
      createPostalRef.current?.focus();
      return;
    }
    if (createError.includes("email or phone")) {
      createPhoneRef.current?.focus();
      return;
    }

    createErrorRef.current?.focus();
  }, [createError, isCreateModalOpen]);

  useLayoutEffect(() => {
    if (!pendingCreateSuccessFocusRef.current || isCreateModalOpen) return;
    const node = createSuccessRef.current;
    if (!node) return;
    pendingCreateSuccessFocusRef.current = false;
    node.focus();
  }, [createSuccess, isCreateModalOpen]);

  return (
    <div className="cn-cases-page">
      <section className="cn-cases-hero">
        <div>
          <p className="cn-cases-eyebrow">Navigator cases</p>
          <h1>Case queue</h1>

          {data?.user ? (
            <p className="cn-cases-scope">
              Scope: <strong>{data.user.ohtName || "Assigned OHT"}</strong>
              {data.user.ohtCode ? <span> · {data.user.ohtCode}</span> : null}
            </p>
          ) : null}
        </div>

        <div className="cn-cases-actions">
          <button
            type="button"
            className="cn-cases-primary-button"
            onClick={openCreateModal}
          >
            Create manual case
          </button>
          <Link
            className="cn-cases-upload-link"
            to="/portal/community-navigator/uploads"
          >
            Upload case list
          </Link>
        </div>
      </section>

      {createSuccess ? (
        <section
          className="cn-cases-success"
          role="status"
          tabIndex={-1}
          ref={createSuccessRef}
        >
          <p>{createSuccess}</p>
        </section>
      ) : null}

      {assignSuccess ? (
        <section className="cn-cases-success" role="status">
          <p>{assignSuccess}</p>
        </section>
      ) : null}

      {assignError ? (
        <section className="cn-cases-alert" role="alert">
          <h2>Claim could not be completed</h2>
          <p>{assignError}</p>
        </section>
      ) : null}

      {loadError ? (
        <section className="cn-cases-alert" role="alert">
          <h2>Cases could not load</h2>
          <p>{loadError}</p>
        </section>
      ) : null}

      <section className="cn-cases-scope-card" aria-labelledby="cn-cases-scope-heading">
        <h2 id="cn-cases-scope-heading" className="cn-cases-scope-card-label">
          Case scope
        </h2>
        <p className="cn-cases-filter-question">Whose cases am I looking at?</p>
        <div
          className="cn-cases-queue-scope"
          role="group"
          aria-label="Case scope"
        >
          {caseScopeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={
                activeCaseScope === option.value
                  ? "cn-cases-queue-scope-button active"
                  : "cn-cases-queue-scope-button"
              }
              aria-pressed={activeCaseScope === option.value}
              onClick={() => handleCaseScopeChange(option.value)}
            >
              <span className="cn-cases-filter-card-label">{option.label}</span>
              <span className="cn-cases-filter-card-count">
                {formatWorkloadCount(
                  workloadSummary?.scopeCounts?.[option.value]
                )}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section
        className="cn-cases-work-queue-card"
        aria-labelledby="cn-cases-work-queue-heading"
      >
        <h2 id="cn-cases-work-queue-heading" className="cn-cases-scope-card-label">
          Work queue
        </h2>
        <p className="cn-cases-filter-question">What work needs attention?</p>
        <div className="cn-cases-tabs" role="group" aria-label="Work queue">
          {workQueues.map((queue) => (
            <button
              key={queue.id}
              type="button"
              className={
                selectedWorkQueue === queue.id
                  ? "cn-cases-tab active"
                  : "cn-cases-tab"
              }
              aria-pressed={selectedWorkQueue === queue.id}
              onClick={() => handleWorkQueueChange(queue.id)}
            >
              <span className="cn-cases-filter-card-label">{queue.label}</span>
              <span className="cn-cases-filter-card-count">
                {formatWorkloadCount(
                  workloadSummary?.workQueuesByScope?.[activeCaseScope]?.[
                    queue.id
                  ]
                )}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="cn-cases-panel">
        <form className="cn-cases-search-wrap" onSubmit={handleSearchSubmit}>
          <input
            className="cn-cases-search"
            type="search"
            placeholder="Search by name, phone, postal code..."
            aria-label="Search navigator cases"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          {appliedSearchText ? (
            <button
              type="button"
              className="cn-cases-clear-search"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          ) : (
            <button type="submit" className="cn-cases-search-button">
              Search
            </button>
          )}
        </form>

        <div className="cn-cases-filter-row">
          <label>
            <span>Source</span>
            <select
              value={selectedSourceType}
              onChange={(event) => handleSourceTypeChange(event.target.value)}
            >
              {sourceFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Priority</span>
            <select
              value={selectedPriorityLevel}
              onChange={(event) =>
                handlePriorityLevelChange(event.target.value)
              }
            >
              {priorityFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="cn-cases-clear-filters"
            onClick={handleClearFilters}
            disabled={
              selectedWorkQueue === "allActive" &&
              !legacyStatus &&
              !intakeNotStartedFilter &&
              !selectedSourceType &&
              !selectedPriorityLevel &&
              !appliedSearchText &&
              !searchText
            }
          >
            Reset filters
          </button>
        </div>

        {activeUploadBatchId !== null ? (
          <div
            className="cn-cases-upload-filter"
            aria-label={`Filtered to upload batch ${activeUploadBatchId}`}
          >
            <span className="cn-cases-upload-filter-label">
              Upload batch #{activeUploadBatchId}
            </span>
            <button
              type="button"
              className="cn-cases-upload-filter-clear"
              onClick={handleClearUploadBatchFilter}
            >
              Clear filter
            </button>
          </div>
        ) : null}

        <div className="cn-cases-result-summary">
          {isLoading ? (
            <span>Loading cases...</span>
          ) : (
            <span>
              {total === 0 ? (
                <>
                  Showing <strong>0</strong> of <strong>0</strong> navigator
                  cases
                </>
              ) : (
                <>
                  Showing{" "}
                  <strong>
                    {rangeStart}–{rangeEnd}
                  </strong>{" "}
                  of <strong>{total}</strong> navigator cases
                </>
              )}
              {workQueueLabel(selectedWorkQueue)
                ? ` · ${workQueueLabel(selectedWorkQueue)}`
                : ""}
              {selectedSourceType
                ? ` · ${formatSource(selectedSourceType)}`
                : ""}
              {selectedPriorityLevel ? ` · ${selectedPriorityLevel}` : ""}
              {appliedSearchText ? ` · Search: "${appliedSearchText}"` : ""}
            </span>
          )}
        </div>

        <div
          className="cn-cases-table-wrap"
          tabIndex={0}
          role="region"
          aria-label="Cases table"
        >
          <table className="cn-cases-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Next action</th>
                <th>Status</th>
                <th>Provider response</th>
                <th>Priority</th>
                <th>Area</th>
                <th>Assigned to</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && items.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="cn-cases-empty-state">
                      {phoneFollowUpRequiredFilter ? (
                        <>
                          <h2>No patients currently require phone follow-up.</h2>
                          <p>
                            When a no-email patient reaches Meet &amp; Greet or
                            Waitlist, they will appear here until you mark them
                            informed.
                          </p>
                        </>
                      ) : (
                        <>
                          <h2>No navigator cases found</h2>
                          <p>
                            There are no cases in this queue yet. New cases will
                            appear here after a public navigator-help request, OHT
                            list upload, or manual navigator entry is created.
                          </p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : null}

              {isLoading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="cn-cases-loading-state">
                      Loading navigator cases...
                    </div>
                  </td>
                </tr>
              ) : null}

              {!isLoading
                ? items.map((item) => {
                    const lastActivity = getQueueLastActivity(item);
                    const caseUrl = `/portal/community-navigator/cases/${item.navigatorCaseId}`;
                    const canAssignToMe =
                      !item.assignedNavigatorUserId &&
                      item.status !== "Closed" &&
                      assigningCaseId !== item.navigatorCaseId;
                    const providerResponseLabel =
                      getProviderResponseLabel(item);
                    const nextActionDisplay = getQueueNextActionDisplay(item);
                    const queuePhone = isPhoneFollowUpQueueItem(item)
                        ? getQueuePatientPhone(item)
                        : null;

                    return (
                      <tr
                        key={item.navigatorCaseId}
                        className="cn-cases-row"
                        onClick={(event) => {
                          const target = event.target as HTMLElement | null;
                          if (
                            target?.closest(
                              "a, button, input, select, textarea, label"
                            )
                          ) {
                            return;
                          }
                          navigate(caseUrl, { state: caseDetailLinkState });
                        }}
                      >
                        <td>
                          <Link
                            className="cn-cases-patient-link"
                            to={caseUrl}
                            state={caseDetailLinkState}
                          >
                            <span className="cn-cases-patient-name">
                              {formatPatientName(item)}
                            </span>
                            <span className="cn-cases-patient-meta">
                              {formatPatientMeta(item)}
                            </span>
                          </Link>
                          {queuePhone ? (
                            <span className="cn-cases-patient-phone">
                              {queuePhone}
                            </span>
                          ) : null}
                          {isManualNoEmailCase(item) ? (
                            <span className="cn-cases-mini-badge">
                              No email
                            </span>
                          ) : null}
                        </td>
                        <td>
                          {nextActionDisplay ? (
                            <span
                              className={getNextActionClassName(
                                item.nextActionCode
                              )}
                            >
                              {nextActionDisplay}
                            </span>
                          ) : (
                            <span className="cn-cases-muted">—</span>
                          )}
                        </td>
                        <td>
                          <span className={getStatusClassName(item.status)}>
                            {formatStatus(item.status)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={getProviderResponseClassName(
                              providerResponseLabel
                            )}
                          >
                            {providerResponseLabel}
                          </span>
                        </td>
                        <td>
                          {item.priorityLevel ? (
                            <span
                              className={getPriorityClassName(
                                item.priorityLevel
                              )}
                            >
                              {item.priorityLevel}
                            </span>
                          ) : (
                            <span className="cn-cases-muted">—</span>
                          )}
                        </td>
                        <td>{formatArea(item)}</td>
                        <td>
                          <div className="cn-cases-assignment-stack">
                            <span className="cn-cases-assignee">
                              {getAssigneeLabel(item, currentUserId)}
                            </span>
                            {canAssignToMe ? (
                              <button
                                type="button"
                                className="cn-cases-open-button"
                                onClick={() =>
                                  handleAssignToMe(item.navigatorCaseId)
                                }
                              >
                                Claim case
                              </button>
                            ) : null}
                            {assigningCaseId === item.navigatorCaseId ? (
                              <span className="cn-cases-muted">
                                Claiming...
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <span className="cn-cases-activity-label">
                            {lastActivity.label}
                          </span>
                          <span>{formatDateTime(lastActivity.timestamp)}</span>
                        </td>
                      </tr>
                    );
                  })
                : null}
            </tbody>
          </table>
        </div>

        {showPager ? (
          <div className="cn-cases-pagination" aria-label="Cases pagination">
            <button
              type="button"
              className="cn-cases-pagination-button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={isLoading || currentPage <= 1}
            >
              Previous
            </button>

            <span className="cn-cases-pagination-status">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="cn-cases-pagination-button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={isLoading || currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>

      {isCreateModalOpen ? (
        <div
          className="cn-cases-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeCreateModal();
            }
          }}
        >
          <section
            className="cn-cases-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cn-create-case-title"
            tabIndex={-1}
            ref={createDialogRef}
          >
            <div className="cn-cases-modal-header">
              <div>
                <p className="cn-cases-eyebrow">Manual navigator entry</p>
                <h2 id="cn-create-case-title">Create manual case</h2>
                <p>
                  Use this for patients who need navigator support and may not
                  have email access.
                </p>
              </div>

              <button
                type="button"
                className="cn-cases-modal-close"
                onClick={closeCreateModal}
                disabled={isCreating}
                aria-label="Close create manual case dialog"
              >
                ×
              </button>
            </div>

            {createError ? (
              <div
                className="cn-cases-modal-error"
                role="alert"
                tabIndex={-1}
                ref={createErrorRef}
              >
                {createError}
              </div>
            ) : null}

            <form
              className="cn-cases-modal-form"
              onSubmit={handleCreateManualCase}
            >
              <div className="cn-cases-form-grid">
                <label>
                  <span>First name *</span>
                  <input
                    ref={createFirstNameRef}
                    type="text"
                    value={manualCaseForm.firstName}
                    onChange={(event) =>
                      updateManualCaseForm("firstName", event.target.value)
                    }
                    autoComplete="given-name"
                    required
                  />
                </label>

                <label>
                  <span>Last name *</span>
                  <input
                    ref={createLastNameRef}
                    type="text"
                    value={manualCaseForm.lastName}
                    onChange={(event) =>
                      updateManualCaseForm("lastName", event.target.value)
                    }
                    autoComplete="family-name"
                    required
                  />
                </label>

                <label>
                  <span>Date of birth *</span>
                  <input
                    ref={createDobRef}
                    type="date"
                    value={manualCaseForm.dateOfBirth}
                    onChange={(event) =>
                      updateManualCaseForm("dateOfBirth", event.target.value)
                    }
                    required
                  />
                </label>

                <label>
                  <span>Postal code *</span>
                  <input
                    ref={createPostalRef}
                    type="text"
                    value={manualCaseForm.postalCode}
                    onChange={(event) =>
                      updateManualCaseForm("postalCode", event.target.value)
                    }
                    placeholder="e.g. L6M 2X1"
                    autoComplete="postal-code"
                    required
                  />
                </label>

                <label>
                  <span>City</span>
                  <select
                    value={manualCaseForm.patientCityId}
                    onChange={(event) =>
                      updateManualCaseForm("patientCityId", event.target.value)
                    }
                    disabled={
                      isCreating ||
                      isLoadingManualCities ||
                      Boolean(manualCitiesError)
                    }
                  >
                    <option value="">
                      {isLoadingManualCities
                        ? "Loading cities…"
                        : "Select city"}
                    </option>
                    {manualCityOptions.map((city) => (
                      <option key={city.CityID} value={String(city.CityID)}>
                        {city.Name}
                      </option>
                    ))}
                  </select>
                  {manualCitiesError ? (
                    <small className="cn-cases-field-hint">
                      {manualCitiesError}
                    </small>
                  ) : null}
                </label>

                <label>
                  <span>Phone number</span>
                  <input
                    ref={createPhoneRef}
                    type="tel"
                    value={manualCaseForm.phoneNumber}
                    onChange={(event) =>
                      updateManualCaseForm("phoneNumber", event.target.value)
                    }
                    placeholder="Required if no email"
                    autoComplete="tel"
                  />
                </label>

                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    value={manualCaseForm.email}
                    onChange={(event) =>
                      updateManualCaseForm("email", event.target.value)
                    }
                    placeholder="Optional"
                    autoComplete="email"
                  />
                </label>

                <label>
                  <span>Gender</span>
                  <select
                    value={manualCaseForm.gender}
                    onChange={(event) =>
                      updateManualCaseForm("gender", event.target.value)
                    }
                  >
                    <option value="">Not specified</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Non-binary">Non-binary</option>
                    <option value="Prefer not to say">
                      Prefer not to say
                    </option>
                  </select>
                </label>

                <label>
                  <span>Navigator triage priority</span>
                  <select
                    value={manualCaseForm.priorityLevel}
                    onChange={(event) =>
                      updateManualCaseForm("priorityLevel", event.target.value)
                    }
                  >
                    <option value="">Not specified</option>
                    <option value="Urgent">Urgent</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </label>
              </div>

              <label className="cn-cases-form-full">
                <span>Source label</span>
                <input
                  type="text"
                  value={manualCaseForm.sourceLabel}
                  onChange={(event) =>
                    updateManualCaseForm("sourceLabel", event.target.value)
                  }
                  placeholder="Manual navigator entry"
                />
              </label>

              <label className="cn-cases-form-full">
                <span>Navigator note</span>
                <textarea
                  value={manualCaseForm.note}
                  onChange={(event) =>
                    updateManualCaseForm("note", event.target.value)
                  }
                  placeholder="Optional internal note"
                  rows={4}
                />
              </label>

              <div className="cn-cases-no-email-note">
                If email is blank, the system will create a no-email patient
                lead and use the phone number for navigator follow-up.
              </div>

              <div className="cn-cases-modal-actions">
                <button
                  type="button"
                  className="cn-cases-secondary-button"
                  onClick={closeCreateModal}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cn-cases-primary-button"
                  disabled={isCreating}
                >
                  {isCreating ? "Creating..." : "Create case"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}